import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import { ArrowLeft, User, Phone, MapPin, Calendar, BookOpen, Heart, Activity, TrendingUp, CheckCircle2, XCircle, Clock, List, CalendarDays, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AttendanceCalendar from './AttendanceCalendar';

const StudentProfile = ({ userRole }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('attendance');

  // History data states
  const [attendanceData, setAttendanceData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [activitiesData, setActivitiesData] = useState(null);
  const [resultsData, setResultsData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState({
    attendance: false,
    health: false,
    activities: false,
    results: false
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, or WEBP');
      return;
    }

    // Validate size (max 150KB)
    if (file.size > 150 * 1024) {
      toast.error('File too large. Maximum size: 150KB');
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post(`/v1/admin/students/${studentId}/photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('✅ Profile picture updated successfully!');
        setStudent(prev => ({ ...prev, photo_url: response.data.photo_url }));
      }
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  useEffect(() => {
    fetchStudentDetails();
    fetchStudentHistory();
  }, [studentId]);

  const fetchStudentDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/v1/admin/students/${studentId}`);
      setStudent(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load student details');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentHistory = async () => {
    // Fetch all history data in parallel
    const promises = [
      fetchAttendanceHistory(),
      fetchHealthHistory(),
      fetchActivitiesHistory(),
      fetchResultsHistory()
    ];
    await Promise.all(promises);
  };

  const fetchAttendanceHistory = async () => {
    try {
      setHistoryLoading(prev => ({ ...prev, attendance: true }));
      const response = await apiClient.get(`/v1/admin/student-history/attendance/${studentId}`);
      setAttendanceData(response.data);
    } catch (error) {
      console.error('Failed to load attendance:', error);
    } finally {
      setHistoryLoading(prev => ({ ...prev, attendance: false }));
    }
  };

  const fetchHealthHistory = async () => {
    try {
      setHistoryLoading(prev => ({ ...prev, health: true }));
      const response = await apiClient.get(`/v1/admin/student-history/health/${studentId}`);
      setHealthData(response.data);
    } catch (error) {
      console.error('Failed to load health:', error);
    } finally {
      setHistoryLoading(prev => ({ ...prev, health: false }));
    }
  };

  const fetchActivitiesHistory = async () => {
    try {
      setHistoryLoading(prev => ({ ...prev, activities: true }));
      const response = await apiClient.get(`/v1/admin/student-history/activities/${studentId}`);
      setActivitiesData(response.data);
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setHistoryLoading(prev => ({ ...prev, activities: false }));
    }
  };

  const fetchResultsHistory = async () => {
    try {
      setHistoryLoading(prev => ({ ...prev, results: true }));
      const response = await apiClient.get(`/v1/admin/student-history/results/${studentId}`);
      setResultsData(response.data);
    } catch (error) {
      console.error('Failed to load results:', error);
    } finally {
      setHistoryLoading(prev => ({ ...prev, results: false }));
    }
  };

  const handleBack = () => {
    if (userRole === 'admin') {
      navigate('/admin/students');
    } else if (userRole === 'warden') {
      navigate('/warden/students');
    } else {
      navigate('/parent');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error || 'Student not found'}
        </div>
        <button
          onClick={handleBack}
          className="mt-4 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Students
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Student Profile</h1>
      </div>

      {/* Student Overview Card */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-start gap-6">
          {/* Photo */}
          <div className="flex-shrink-0 relative group">
            {student.photo_url ? (
              <img
                src={student.photo_url}
                alt={student.name}
                className={`w-32 h-32 rounded-full object-cover border-4 border-blue-100 shadow-md transition-all duration-300 ${uploadingPhoto ? 'brightness-50 scale-105 animate-pulse' : 'group-hover:brightness-75 group-hover:scale-105'
                  }`}
              />
            ) : (
              <div className={`w-32 h-32 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-md transition-all duration-300 ${uploadingPhoto ? 'brightness-50 scale-105 animate-pulse' : 'group-hover:brightness-75 group-hover:scale-105'
                }`}>
                <User className="w-16 h-16 text-white" />
              </div>
            )}

            {/* Admin Upload overlay */}
            {userRole === 'admin' && (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className={`absolute inset-0 flex flex-col items-center justify-center rounded-full transition-all duration-300 focus:outline-none ${uploadingPhoto
                      ? 'bg-black/60 text-white opacity-100 cursor-wait'
                      : 'bg-black/45 text-white opacity-0 group-hover:opacity-100 cursor-pointer'
                    }`}
                >
                  {uploadingPhoto ? (
                    <div className="flex flex-col items-center gap-1.5">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-blue-200 animate-pulse">Uploading</span>
                    </div>
                  ) : (
                    <>
                      <Camera className="w-7 h-7 mb-1 text-white/90" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-white/90">Change Photo</span>
                    </>
                  )}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                />
              </>
            )}
          </div>

          {/* Basic Info */}
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{student.name}</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Student ID:</span>
                <span className="ml-2 font-semibold text-blue-600">{student.readable_id || student.id}</span>
              </div>
              <div>
                <span className="text-gray-600">Age:</span>
                <span className="ml-2 font-semibold">{student.age} years</span>
              </div>
              <div>
                <span className="text-gray-600">Gender:</span>
                <span className="ml-2 font-semibold capitalize">{student.gender || 'Not set'}</span>
              </div>
              <div>
                <span className="text-gray-600">Birth Date:</span>
                <span className="ml-2 font-semibold">{student.dob || student.date_of_birth || 'Not set'}</span>
              </div>
              <div>
                <span className="text-gray-600">Class:</span>
                <span className="ml-2 font-semibold">{student.class_name}</span>
              </div>
              <div>
                <span className="text-gray-600">Room:</span>
                <span className="ml-2 font-semibold">{student.room_number || 'N/A'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-600">Assigned Wardens:</span>
                {Array.isArray(student.assigned_wardens) && student.assigned_wardens.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {student.assigned_wardens.map((w, idx) => (
                      <span key={w.id || idx} className="text-xs px-2.5 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                        {w.name} {idx === 0 ? '(Primary)' : ''} {w.phone ? `• ${w.phone}` : ''}
                      </span>
                    ))}
                  </div>
                ) : (student.warden_name || student.warden_info?.name) ? (
                  <div className="mt-1 flex flex-wrap gap-2">
                    <span className="text-xs px-2.5 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                      {student.warden_name || student.warden_info.name} (Primary)
                    </span>
                    {(student.secondary_warden_name || student.secondary_warden_info?.name) && (
                      <span className="text-xs px-2.5 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                        {student.secondary_warden_name || student.secondary_warden_info.name}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="ml-2 font-semibold text-gray-500">Not Assigned</span>
                )}
              </div>
            </div>

            {/* Address */}
            {student.address && (
              <div className="mt-4 flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-500 mt-1" />
                <div>
                  <span className="text-gray-600 text-sm">Home Address:</span>
                  <p className="text-sm font-medium">{student.address}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Interests Section */}
      {student.interests && student.interests.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-pink-500" />
            <h3 className="text-xl font-semibold">Interests & Hobbies</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {student.interests.map((interest, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-pink-50 text-pink-700 rounded-full text-sm font-medium"
              >
                {interest}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Attendance Stat */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">Attendance</div>
          {attendanceData && !historyLoading.attendance ? (
            <>
              <div className="text-2xl font-bold text-green-600">
                {attendanceData.stats?.attendance_percentage || 0}%
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {attendanceData.stats?.present || 0}/{attendanceData.stats?.total || 0} days present
              </div>
            </>
          ) : (
            <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
          )}
        </div>

        {/* Health Stat */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">Health Status</div>
          {healthData && !historyLoading.health ? (
            <>
              <div className="text-lg font-bold text-blue-600 capitalize">
                {healthData.latest_status?.replace('_', ' ') || 'Healthy'}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {healthData.total_records || 0} records
              </div>
            </>
          ) : (
            <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
          )}
        </div>

        {/* Activities Stat */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">Activities</div>
          {activitiesData && !historyLoading.activities ? (
            <>
              <div className="text-2xl font-bold text-purple-600">
                {activitiesData.total_activities || 0}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Total activities logged
              </div>
            </>
          ) : (
            <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
          )}
        </div>

        {/* Results Stat */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 mb-1">Academic Average</div>
          {resultsData && !historyLoading.results ? (
            <>
              <div className="text-2xl font-bold text-orange-600">
                {resultsData.average_percentage || 0}%
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {resultsData.total_exams || 0} exams
              </div>
            </>
          ) : (
            <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-t-lg shadow">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('attendance')}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'attendance'
                ? 'border-green-500 text-green-600 bg-green-50'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
          >
            <Calendar className="w-4 h-4 inline-block mr-2" />
            Attendance
          </button>
          <button
            onClick={() => setActiveTab('health')}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'health'
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
          >
            <Activity className="w-4 h-4 inline-block mr-2" />
            Health
          </button>
          <button
            onClick={() => setActiveTab('activities')}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'activities'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
          >
            <Heart className="w-4 h-4 inline-block mr-2" />
            Activities
          </button>
          <button
            onClick={() => setActiveTab('results')}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'results'
                ? 'border-orange-500 text-orange-600 bg-orange-50'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
          >
            <TrendingUp className="w-4 h-4 inline-block mr-2" />
            Results
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-b-lg shadow p-6 mb-6">
        {activeTab === 'attendance' && (
          <AttendanceTab data={attendanceData} loading={historyLoading.attendance} />
        )}
        {activeTab === 'health' && (
          <HealthTab data={healthData} loading={historyLoading.health} />
        )}
        {activeTab === 'activities' && (
          <ActivitiesTab data={activitiesData} loading={historyLoading.activities} />
        )}
        {activeTab === 'results' && (
          <ResultsTab data={resultsData} loading={historyLoading.results} />
        )}
      </div>

      {/* Action Buttons for Admin/Warden */}
      {(userRole === 'admin' || userRole === 'warden') && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Actions</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate(`/admin/students/edit/${studentId}`)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Edit Student
            </button>
            {userRole === 'admin' && (
              <>
                <button
                  onClick={() => navigate(`/admin/warden-assignment?student=${studentId}`)}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Assign Warden
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Tab Components
const AttendanceTab = ({ data, loading }) => {
  const [viewMode, setViewMode] = React.useState('list'); // 'list' or 'calendar'

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div></div>;
  }

  if (!data || !data.records || data.records.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p>No attendance records yet</p>
      </div>
    );
  }

  // Remove duplicates (keep most recent record for each date)
  const uniqueRecords = {};
  data.records.forEach(record => {
    const dateKey = record.date;
    if (!uniqueRecords[dateKey] || new Date(record.created_at || 0) > new Date(uniqueRecords[dateKey].created_at || 0)) {
      uniqueRecords[dateKey] = record;
    }
  });

  const deduplicatedRecords = Object.values(uniqueRecords);

  // Group records by month
  const recordsByMonth = deduplicatedRecords.reduce((acc, record) => {
    const month = new Date(record.date).toLocaleString('default', { month: 'long', year: 'numeric' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(record);
    return acc;
  }, {});

  return (
    <div>
      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm text-green-700 mb-1">Present</div>
          <div className="text-2xl font-bold text-green-600">{data.stats?.present || 0}</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="text-sm text-red-700 mb-1">Absent</div>
          <div className="text-2xl font-bold text-red-600">{data.stats?.absent || 0}</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="text-sm text-yellow-700 mb-1">Late</div>
          <div className="text-2xl font-bold text-yellow-600">{data.stats?.late || 0}</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-blue-700 mb-1">Attendance Rate</div>
          <div className="text-2xl font-bold text-blue-600">{data.stats?.attendance_percentage || 0}%</div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Attendance History</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${viewMode === 'list'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            <List className="w-4 h-4" />
            List View
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${viewMode === 'calendar'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            <CalendarDays className="w-4 h-4" />
            Calendar View
          </button>
        </div>
      </div>

      {/* View Content */}
      {viewMode === 'calendar' ? (
        <AttendanceCalendar attendanceRecords={deduplicatedRecords} />
      ) : (
        <div className="space-y-6">
          {Object.entries(recordsByMonth).map(([month, records]) => (
            <div key={month}>
              <h4 className="font-semibold text-gray-800 mb-3">{month}</h4>
              <div className="grid grid-cols-7 gap-2">
                {records.map((record) => (
                  <div
                    key={record.date}
                    className={`p-3 rounded-lg border text-center ${record.status === 'present'
                        ? 'bg-green-50 border-green-200'
                        : record.status === 'absent'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-yellow-50 border-yellow-200'
                      }`}
                  >
                    <div className="text-xs text-gray-600 mb-1">
                      {new Date(record.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                    </div>
                    <div className="text-xs font-medium capitalize flex items-center justify-center gap-1">
                      {record.status === 'present' && <CheckCircle2 className="w-3 h-3 text-green-600" />}
                      {record.status === 'absent' && <XCircle className="w-3 h-3 text-red-600" />}
                      {record.status === 'late' && <Clock className="w-3 h-3 text-yellow-600" />}
                      {record.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HealthTab = ({ data, loading }) => {
  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>;
  }

  if (!data || !data.records || data.records.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Activity className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p>No health records yet</p>
      </div>
    );
  }

  const statusColors = {
    healthy: 'bg-green-50 border-green-200 text-green-800',
    doing_well: 'bg-green-50 border-green-200 text-green-800',
    minor_issue: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    sick: 'bg-orange-50 border-orange-200 text-orange-800',
    hospital_visit: 'bg-red-50 border-red-200 text-red-800'
  };

  return (
    <div>
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          <div>
            <div className="text-sm text-blue-700">Current Health Status</div>
            <div className="text-lg font-bold text-blue-900 capitalize">
              {data.latest_status?.replace('_', ' ') || 'Healthy'}
            </div>
          </div>
        </div>
      </div>

      {/* Health Timeline */}
      <div className="space-y-4">
        <h4 className="font-semibold text-gray-800">Health Timeline</h4>
        {data.records.map((record, idx) => {
          const symptoms = record.symptoms || record.notes || record.description;
          const treatment = record.treatment || record.visit_details;
          return (
            <div key={idx} className={`p-4 rounded-lg border ${statusColors[record.status] || statusColors.doing_well}`}>
              <div className="flex justify-between items-start mb-2">
                <div className="font-medium capitalize">{record.status?.replace('_', ' ')}</div>
                <div className="text-sm text-gray-600">
                  {new Date(record.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              {symptoms && (
                <div className="mt-2 text-sm border-t border-current/10 pt-2">
                  <span className="font-semibold">Symptoms/Notes:</span>{" "}
                  <span className="text-gray-700">{symptoms}</span>
                </div>
              )}
              {treatment && (
                <div className={`mt-2 text-sm ${!symptoms ? 'border-t border-current/10 pt-2' : ''}`}>
                  <span className="font-semibold">Treatment/Details:</span>{" "}
                  <span className="text-gray-700">{treatment}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ActivitiesTab = ({ data, loading }) => {
  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div></div>;
  }

  if (!data || !data.records || data.records.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Heart className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p>No activities logged yet</p>
      </div>
    );
  }

  const activityTypeColors = {
    sports: 'bg-blue-100 text-blue-800 border-blue-300',
    arts: 'bg-pink-100 text-pink-800 border-pink-300',
    academic: 'bg-green-100 text-green-800 border-green-300',
    cultural: 'bg-purple-100 text-purple-800 border-purple-300',
    other: 'bg-gray-100 text-gray-800 border-gray-300'
  };

  return (
    <div>
      {/* Activity Type Summary */}
      <div className="mb-6 flex flex-wrap gap-3">
        {Object.entries(data.by_type || {}).map(([type, count]) => (
          <div key={type} className={`px-4 py-2 rounded-lg border ${activityTypeColors[type] || activityTypeColors.other}`}>
            <span className="font-medium capitalize">{type}</span>
            <span className="ml-2 font-bold">{count}</span>
          </div>
        ))}
      </div>

      {/* Activities List */}
      <div className="space-y-3">
        <h4 className="font-semibold text-gray-800">Activity History</h4>
        {data.records.map((activity, idx) => (
          <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${activityTypeColors[activity.activity_type] || activityTypeColors.other}`}>
                    {activity.activity_type || 'other'}
                  </span>
                  <span className="text-sm text-gray-600">
                    {new Date(activity.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <p className="text-gray-800 font-medium">{activity.title || activity.description}</p>
                {activity.description && activity.title && (
                  <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ResultsTab = ({ data, loading }) => {
  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div></div>;
  }

  if (!data || !data.records || data.records.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p>No exam results yet</p>
      </div>
    );
  }

  return (
    <div>
      {/* Overall Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <div className="text-sm text-orange-700 mb-1">Average Percentage</div>
          <div className="text-3xl font-bold text-orange-600">{data.average_percentage || 0}%</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-sm text-blue-700 mb-1">Total Exams</div>
          <div className="text-3xl font-bold text-blue-600">{data.total_exams || 0}</div>
        </div>
      </div>

      {/* Results by Exam Type */}
      {data.by_exam_type && Object.keys(data.by_exam_type).length > 0 && (
        <div className="space-y-6">
          {Object.entries(data.by_exam_type).map(([examType, results]) => (
            <div key={examType}>
              <h4 className="font-semibold text-gray-800 mb-3 capitalize">{examType}</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Subject</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Marks</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Total</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Percentage</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Grade</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {results?.map((result, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm capitalize">{result.subject_name || result.subject || '-'}</td>
                        <td className="px-4 py-2 text-sm font-medium">{result.marks_obtained}</td>
                        <td className="px-4 py-2 text-sm">{result.total_marks}</td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`font-medium ${result.percentage >= 90 ? 'text-green-600' :
                              result.percentage >= 75 ? 'text-blue-600' :
                                result.percentage >= 60 ? 'text-yellow-600' :
                                  'text-red-600'
                            }`}>
                            {result.percentage}%
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm font-medium">{result.grade || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* All Results Flat View */}
      {(!data.by_exam_type || Object.keys(data.by_exam_type).length === 0) && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Exam Type</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Subject</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Marks</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Percentage</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Grade</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.records.map((result, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm capitalize">{result.exam_type}</td>
                  <td className="px-4 py-2 text-sm">{result.subject}</td>
                  <td className="px-4 py-2 text-sm">{result.marks_obtained}/{result.total_marks}</td>
                  <td className="px-4 py-2 text-sm font-medium">{result.percentage}%</td>
                  <td className="px-4 py-2 text-sm">{result.grade || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StudentProfile;